import { CSSProperties, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { HERO_ANIMATION_STATES } from 'Utilities/animation';
import { usePrevious } from 'Utilities/hooks';

import { fireInferredCookieConsent } from 'Components/CookieBanner/cookieConsent.constants';

import { useAuthUI } from '../../../experiments/useAuthUI/useAuthUI';

type useHeroHelpersProps = {
  animateFromHomePage?: boolean;
  container?: React.RefObject<HTMLDivElement | null>;
};
export const useHeroHelpers = ({ animateFromHomePage = false, container }: useHeroHelpersProps) => {
  const history = useHistory();

  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const { userState } = useContext(UserContext);
  const { isAuthUIJourney } = useAuthUI();

  const user = Number.isInteger(userState?.id);

  const [minHeight, setMinHeight] = useState('auto');
  const [loading, setLoading] = useState(HERO_ANIMATION_STATES.none);

  // Track the container height passively so we never force a synchronous
  // layout read (getBoundingClientRect) during the submit interaction, which
  // would delay the next paint (INP). The latest measured height is kept in a
  // ref and only committed to state when the transition begins.
  const measuredHeightRef = useRef(0);

  useEffect(() => history.listen(() => {
    setLoading(HERO_ANIMATION_STATES.none);
  }), [history]);

  const isVrmTransition = loading === HERO_ANIMATION_STATES.vrm as string;
  const isMileageTransition = loading === HERO_ANIMATION_STATES.mileage as string;

  const [isAnimatingVrmTransition, setIsAnimatingVrmTransition] = useState(false);
  const [isAnimatingMileageTransition, setIsAnimatingMileageTransition] = useState(false);
  const prevIsAnimatingMileageTransition = usePrevious(isAnimatingMileageTransition);
  const [hideVehicleHeader, setHideVehicleHeader] = useState(false);

  const loadingHandler = useCallback((val: any) => setLoading(val), []);

  /**
    * IMPORTANT
    * If altering the height of `.contentWrapper` ensure you alter the min-heights to
    * prevent Google Pagespeed from penalising us for Cumlative Layout Shift
  */

  const cssVars = (loading && { '--lock-opacity-delay': '0.1s', '--vrm-input-opacity-delay': '0.1s' } as CSSProperties) || {};

  // Observe the container's size off the interaction path so the height used
  // to lock the layout during the transition is already available at submit
  // time, avoiding a forced reflow inside the click handler.
  useEffect(() => {
    const node = container?.current;

    if (!node) {
      return undefined;
    }

    if (typeof ResizeObserver === 'undefined') {
      measuredHeightRef.current = node.offsetHeight;
      return undefined;
    }

    measuredHeightRef.current = node.offsetHeight;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (entry) {
        // Prefer the border-box size so this matches the `offsetHeight` seed
        // above (offsetHeight includes padding + border). contentRect is the
        // content box only, so falling back to it would silently undercount if
        // padding/border is ever added to the container.
        measuredHeightRef.current = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      }
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, [container]);

  useEffect(() => {
    if (loading) {
      window.scrollTo({
        behavior: 'smooth',
        left: 0,
        top: 0,
      });
    }
  }, [loading]);

  useEffect(() => {
    if (!user && isAuthUIJourney) {
      setHideVehicleHeader(true);
    }
  }, [isAuthUIJourney, user]);

  useEffect(() => {
    if (isMileageTransition && prevIsAnimatingMileageTransition && !isAnimatingMileageTransition) {
      fireInferredCookieConsent();
      history.push(`/${vehicle.vrm}`, { animate: true, animateFromNewHomePage: animateFromHomePage });
    }
  }, [
    animateFromHomePage,
    isMileageTransition,
    prevIsAnimatingMileageTransition,
    isAnimatingMileageTransition,
    vehicle?.vrm,
    history,
  ]);

  useEffect(() => {
    if (loading && minHeight === 'auto') {
      setMinHeight(`${measuredHeightRef.current}px`);
    }
    if (isVrmTransition) {
      setIsAnimatingVrmTransition(true);
    } else if (isMileageTransition) {
      setIsAnimatingMileageTransition(true);
    }
  }, [loading, minHeight, isVrmTransition, isMileageTransition, container]);

  return {
    cssVars,
    hideVehicleHeader,
    isAnimatingMileageTransition,
    isAnimatingVrmTransition,
    isMileageTransition,
    loading,
    loadingHandler,
    minHeight,
    prevIsAnimatingMileageTransition,
    setIsAnimatingMileageTransition,
    setIsAnimatingVrmTransition,
  };
};
